import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Menu, Transition } from '@headlessui/react';
import { DotsThreeVertical } from '@phosphor-icons/react';

function CardMenu({ options }) {
  const handleClick =
    ({ onClick, ...rest }) =>
    (e) => {
      e.preventDefault();
      if (onClick) onClick(rest);
    };
  return (
    <Menu as="div" className="relative flex-shrink-0 pr-2">
      {({ open }) => (
        <>
          <Menu.Button
            aria-label="Open options"
            className="w-7 h-7 inline-flex items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600"
          >
            <span className="sr-only">Open options</span>
            <DotsThreeVertical size={20} aria-hidden="true" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            show={open}
          >
            <Menu.Items
              static
              className="z-10 mx-3 absolute origin-top-right right-0 w-48 mt-1 rounded-lg bg-white border border-neutral-200 shadow-popover divide-y divide-neutral-200 focus:outline-none"
            >
              <div className="py-1">
                {options.map((item) => (
                  <Menu.Item key={item.id}>
                    {({ active }) => (
                      <a
                        href="/"
                        onClick={handleClick(item)}
                        className={cn(
                          active
                            ? 'bg-neutral-100 text-neutral-900'
                            : 'text-neutral-700',
                          'block px-4 py-2 text-sm',
                        )}
                      >
                        {item.label}
                      </a>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
}

CardMenu.propTypes = {
  options: PropTypes.instanceOf(Array).isRequired,
};

function Card({ className, label, actionOptions, renderAction, children }) {
  return (
    <div
      className={cn('bg-white border border-neutral-200 rounded-md', className)}
    >
      {label ? (
        <div className="px-5 py-3 border-b border-neutral-200">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex items-center gap-1 min-w-0">
              <h3 className="text-sm font-semibold text-neutral-900 truncate">
                {label}
              </h3>
              {Array.isArray(actionOptions) && actionOptions.length > 1 && (
                <CardMenu options={actionOptions} />
              )}
            </div>
            {renderAction && (
              <div className="flex-shrink-0">{renderAction}</div>
            )}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

Card.defaultProps = {
  actionOptions: null,
  renderAction: null,
  children: false,
  className: '',
  label: '',
};

Card.propTypes = {
  className: PropTypes.string,
  renderAction: PropTypes.instanceOf(Object),
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
    PropTypes.number,
  ]),
  actionOptions: PropTypes.oneOfType([
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(Object),
  ]),
  label: PropTypes.string,
};

export default Card;
